﻿import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useRouteError } from 'react-router';
import {
    CheckmarkOutline,
    ChevronDownOutline,
    ChevronRightSolid,
    ChevronUpOutline,
    ClockOutline,
    CloudOffOutline,
    DuplicateOutline,
    EmailOutline,
    ExternalLinkOutline,
    FlightScapeOutline,
    HomeOutline,
    PhoneOutline,
    RefreshOutline,
    SupportOutline,
    WarningMajorOutline
} from '../../../icons/icons';
import { appInsights, getTraceId } from '../../../insights/azure-insights';
import { ROUTES } from '../routes';

export function RootErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();
    const [errorDetailsOpen, setErrorDetailsOpen] = useState(import.meta.env.DEV);
    const [traceId, setTraceId] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (error instanceof Error) {
            appInsights?.trackException({ exception: error });
        }
        setTraceId(getTraceId());
    }, [error]);

    const handleCopyTraceId = () => {
        if (!traceId) {
            return;
        }
        navigator.clipboard.writeText(traceId).then(() => {
            setCopied(true);
            self.setTimeout(() => setCopied(false), 2000);
        });
    };

    const errorMessage = error instanceof Error ? error.message : String(error ?? 'Unknown error');
    const errorStack = error instanceof Error ? error.stack : undefined;

    return (
        <div className={'d-flex flex-column'} style={{ height: '100vh' }}>
            <div className="border-bottom px-3 d-flex justify-content-between primary-bg-color flex-shrink-0" style={{ height: 42 }}>
                <NavLink
                    className="d-flex align-items-center gap-2"
                    to="/"
                    style={{ textDecoration: 'none', color: 'var(--kendo-color-app-on-surface' }}>
                    <SvgIcon icon={FlightScapeOutline} />
                    <span style={{ fontWeight: 700, fontSize: '1.2rem', fontFamily: 'Red Hat Display' }}> Flight Explorer</span>
                </NavLink>
            </div>

            <div className="d-flex flex-column align-items-center flex-grow-1 my-5" style={{ height: '100vh', overflowY: 'auto' }}>
                <div className="d-flex flex-column gap-5">
                    <div className="d-flex flex-column gap-2">
                        <div
                            className={'d-flex align-items-center gap-2 text-secondary'}
                            style={{ fontFamily: 'Red Hat Display', fontSize: '1.2rem' }}>
                            <SvgIcon icon={WarningMajorOutline} size={'xxlarge'} themeColor={'error'} className={'p-1 error-icon-bg'} />
                            Error
                            {traceId && (
                                <div className={'text-secondary'} style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    {traceId}
                                    <Button
                                        svgIcon={copied ? CheckmarkOutline : DuplicateOutline}
                                        fillMode={'flat'}
                                        size={'small'}
                                        title={'Copy reference ID'}
                                        onClick={handleCopyTraceId}>
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className={''} style={{ fontSize: '2rem', fontWeight: 550 }}>
                            Something went wrong
                        </div>
                        <div style={{ maxWidth: 600, fontSize: '1rem' }} className={'text-secondary'}>
                            We encountered an unexpected error. Please try refreshing the page, and if the problem persists, contact
                            support.
                        </div>
                    </div>
                    <div className={'d-flex gap-3 justify-content-between'}>
                        <div className={'d-flex flex-column gap-3'}>
                            <div className={'text-secondary'}>TROUBLESHOOTING</div>
                            <div className={'d-flex flex-column gap-3'}>
                                <div className={'d-flex align-items-center gap-2'} style={{ fontFamily: 'Red Hat Display' }}>
                                    <SvgIcon icon={RefreshOutline} className={'p-1 error-icon-bg'} />
                                    Refresh the page
                                </div>
                                <div className={'d-flex align-items-center gap-2'} style={{ fontFamily: 'Red Hat Display' }}>
                                    <SvgIcon icon={CloudOffOutline} className={'p-1 error-icon-bg'} />
                                    Check your connection
                                </div>
                                <div className={'d-flex align-items-center gap-2'} style={{ fontFamily: 'Red Hat Display' }}>
                                    <SvgIcon icon={ClockOutline} className={'p-1 error-icon-bg'} />
                                    Wait a moment
                                </div>
                            </div>
                            <div className={'d-flex gap-3'}>
                                <Button
                                    svgIcon={RefreshOutline}
                                    themeColor={'primary'}
                                    onClick={() => window.location.reload()}
                                    className={'mt-2'}>
                                    Refresh
                                </Button>
                                {location.pathname !== ROUTES.HOME && (
                                    <Button svgIcon={HomeOutline} onClick={() => navigate(ROUTES.HOME)} className={'mt-2'}>
                                        Home
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className={'text-secondary'}>CONTACT SUPPORT</div>
                            <div className={'k-card mt-2'}>
                                <a
                                    href="https://communityportal.flightservices.cae.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={'d-flex align-items-center gap-2 justify-content-between clickable p-2 border-bottom'}
                                    style={{ fontFamily: 'Red Hat Display', textDecoration: 'none', color: 'inherit' }}>
                                    <div className={'d-flex align-items-center gap-2'}>
                                        <SvgIcon icon={SupportOutline} className={'p-1 error-icon-bg'} size={'xlarge'} />
                                        CAE Self Service Portal
                                    </div>
                                    <SvgIcon icon={ChevronRightSolid} />
                                </a>
                                <a
                                    href="https://www.flightexplorer.com/support/support.aspx"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={'d-flex align-items-center gap-2 justify-content-between clickable p-2 border-bottom'}
                                    style={{ fontFamily: 'Red Hat Display', textDecoration: 'none', color: 'inherit' }}>
                                    <div className={'d-flex align-items-center gap-2'}>
                                        <SvgIcon icon={EmailOutline} className={'p-1 error-icon-bg'} size={'xlarge'} />
                                        Support Request
                                    </div>
                                    <SvgIcon icon={ChevronRightSolid} />
                                </a>
                                <a
                                    href="tel:+18773470026"
                                    className={'d-flex align-items-center gap-2 justify-content-between clickable p-2 border-bottom'}
                                    style={{ fontFamily: 'Red Hat Display', textDecoration: 'none', color: 'inherit' }}>
                                    <div className={'d-flex align-items-center gap-2'}>
                                        <SvgIcon icon={PhoneOutline} className={'p-1 error-icon-bg'} size={'xlarge'} />
                                        US: 877-347-0026
                                    </div>
                                    <SvgIcon icon={ChevronRightSolid} />
                                </a>
                                <a
                                    href="tel:+18773810929"
                                    className={'d-flex align-items-center gap-2 justify-content-between clickable p-2 border-bottom'}
                                    style={{ fontFamily: 'Red Hat Display', textDecoration: 'none', color: 'inherit' }}>
                                    <div className={'d-flex align-items-center gap-2'}>
                                        <SvgIcon icon={PhoneOutline} className={'p-1 error-icon-bg'} size={'xlarge'} />
                                        Canada: 877-381-0929
                                    </div>
                                    <SvgIcon icon={ChevronRightSolid} />
                                </a>
                                <a
                                    href="tel:+12182296401"
                                    className={'d-flex align-items-center gap-2 justify-content-between clickable p-2'}
                                    style={{ fontFamily: 'Red Hat Display', textDecoration: 'none', color: 'inherit' }}>
                                    <div className={'d-flex align-items-center gap-2'}>
                                        <SvgIcon icon={PhoneOutline} className={'p-1 error-icon-bg'} size={'xlarge'} />
                                        Worldwide: +1 218-229-6401
                                    </div>
                                    <SvgIcon icon={ChevronRightSolid} />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className={'border-top pt-4'}>
                        <div className={'d-flex align-items-center justify-content-between'}>
                            <div
                                className={'d-flex align-items-center gap-2 clickable'}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setErrorDetailsOpen((o) => !o)}>
                                <SvgIcon icon={errorDetailsOpen ? ChevronUpOutline : ChevronDownOutline} />
                                <span className={'text-secondary'}>Error details</span>
                            </div>
                            <a
                                href="/system-hub"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={'d-flex align-items-center gap-1 clickable'}
                                style={{ textDecoration: 'none', color: 'inherit' }}>
                                <span className={'text-secondary'}>Check system hub</span>
                                <SvgIcon icon={ExternalLinkOutline} className={'text-secondary'} />
                            </a>
                        </div>
                        {errorDetailsOpen && (
                            <div className={'mt-2 p-2 k-card overflow-auto'} style={{ maxWidth: 700 }}>
                                <div style={{ fontWeight: 600 }}>{errorMessage}</div>
                                {errorStack && (
                                    <pre
                                        className={'text-secondary mt-1'}
                                        style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                                        {errorStack}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
